import { ApiClient } from './client';
import type { Notification } from '@/types/gamification.types';

/**
 * Notifications API Service
 * Uses backend /users/me/notifications
 */

export async function getNotifications(
  _userId: string,
  filters?: {
    isRead?: boolean;
    type?: string;
    limit?: number;
  }
): Promise<Notification[]> {
  const params = new URLSearchParams();
  if (filters?.isRead !== undefined) params.set('is_read', String(filters.isRead));
  if (filters?.limit) params.set('limit', String(Math.min(filters.limit, 50)));
  const query = params.toString();
  return ApiClient.get<Notification[]>(`/users/me/notifications${query ? `?${query}` : ''}`);
}

export async function getUnreadCount(userId: string): Promise<number> {
  const notifications = await getNotifications(userId, { isRead: false });
  return notifications.length;
}

export async function markNotificationAsRead(notificationId: string): Promise<Notification> {
  return ApiClient.patch<Notification>(`/users/me/notifications/${notificationId}/read`, {});
}

export async function markAllNotificationsAsRead(_userId: string): Promise<void> {
  await ApiClient.patch('/users/me/notifications/read-all', {});
}

export async function markNotificationAsUnread(notificationId: string): Promise<Notification> {
  return ApiClient.patch<Notification>(`/users/me/notifications/${notificationId}/unread`, {});
}

export async function deleteNotification(notificationId: string): Promise<void> {
  await ApiClient.delete(`/users/me/notifications/${notificationId}`);
}

export async function getNotificationPreferences(_userId: string): Promise<{
  email: boolean;
  push: boolean;
  storyReminders: boolean;
  achievementAlerts: boolean;
  streakReminders: boolean;
}> {
  const prefs = await ApiClient.get<Record<string, boolean>>('/users/me/preferences');
  return {
    email: prefs?.email ?? true,
    push: prefs?.push ?? true,
    storyReminders: prefs?.storyReminders ?? true,
    achievementAlerts: prefs?.achievementAlerts ?? true,
    streakReminders: prefs?.streakReminders ?? true,
  };
}

export async function updateNotificationPreferences(
  _userId: string,
  preferences: {
    email?: boolean;
    push?: boolean;
    storyReminders?: boolean;
    achievementAlerts?: boolean;
    streakReminders?: boolean;
  }
): Promise<void> {
  await ApiClient.patch('/users/me/preferences', preferences);
}
